import {
  Activity,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Gauge,
  Globe2,
  LoaderCircle,
  Play,
  Server,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  getHealthChecks,
  getOperationsSummary,
  runQodeHealthCheck,
} from "../api";
import { formatRelativeTime } from "../lib/format";
import type {
  HealthCheckItem,
  IncidentItem,
  QodeItem,
  OperationsSummary,
  TimelineEvent,
  WorkspaceCapabilities,
} from "../types";

type InsightsViewProps = {
  capabilities: WorkspaceCapabilities;
  qodes: QodeItem[];
  workspaceId: string;
  onIncidentChanged: (incident: IncidentItem) => void;
  onQodeChanged: (qode: QodeItem, event?: TimelineEvent | null) => void;
};

export function InsightsView({
  capabilities,
  qodes,
  workspaceId,
  onIncidentChanged,
  onQodeChanged,
}: InsightsViewProps) {
  const [checks, setChecks] = useState<HealthCheckItem[]>([]);
  const [operations, setOperations] = useState<OperationsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setChecks([]);
    setOperations(null);
    setLoading(true);
    setError("");
    Promise.all([getHealthChecks(), getOperationsSummary()])
      .then(([response, summary]) => {
        if (active) {
          setChecks(response.healthChecks);
          setOperations(summary);
        }
      })
      .catch((loadError) => {
        if (active) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "无法读取健康检查记录。",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);

  const monitorableQodes = qodes.filter(
    (qode) => qode.status === "active" || qode.status === "degraded",
  );
  const latestByQode = useMemo(() => {
    const latest = new Map<string, HealthCheckItem>();
    for (const check of checks) {
      if (!latest.has(check.qode.id)) latest.set(check.qode.id, check);
    }
    return latest;
  }, [checks]);
  const latestChecks = Array.from(latestByQode.values());
  const healthyCount = latestChecks.filter(
    (check) => check.status === "healthy",
  ).length;
  const attentionCount = latestChecks.filter(
    (check) => check.status !== "healthy",
  ).length;
  const averageLatency = latestChecks.length
    ? Math.round(
        latestChecks.reduce((sum, check) => sum + check.latencyMs, 0) /
          latestChecks.length,
      )
    : 0;

  const runCheck = async (qode: QodeItem) => {
    setWorking(qode.id);
    setError("");
    try {
      const response = await runQodeHealthCheck(qode.id);
      setChecks((current) => [
        response.check,
        ...current.filter((item) => item.id !== response.check.id),
      ]);
      onQodeChanged(response.qode);
      if (response.incident) onIncidentChanged(response.incident);
    } catch (actionError) {
      setError(
        actionError instanceof Error
          ? actionError.message
          : "健康检查未能完成。",
      );
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="page insights-page">
      <section className="page-heading">
        <div>
          <h1>Insights</h1>
          <p>查看确定性健康信号，并在连续异常形成 Incident 前核对证据。</p>
        </div>
        <div className="monitor-state">
          <Activity size={16} />
          Monitor 每 5 分钟检查
        </div>
      </section>

      <section className="monitor-summary">
        <div>
          <Server size={17} />
          <span>监测入口</span>
          <strong>{monitorableQodes.length}</strong>
        </div>
        <div>
          <CheckCircle2 size={17} />
          <span>最近正常</span>
          <strong>{healthyCount}</strong>
        </div>
        <div>
          <CircleAlert size={17} />
          <span>需要关注</span>
          <strong>{attentionCount}</strong>
        </div>
        <div>
          <Gauge size={17} />
          <span>平均延迟</span>
          <strong>{averageLatency ? `${averageLatency} ms` : "—"}</strong>
        </div>
      </section>

      {operations ? (
        <section className="signal-summary" aria-label="工作区运行信号汇总">
          <div>
            <span>DNS 异常</span>
            <strong>{operations.signals.sources.dns}</strong>
          </div>
          <div>
            <span>TLS 预警</span>
            <strong>{operations.signals.sources.tls}</strong>
          </div>
          <div>
            <span>HTTP 异常</span>
            <strong>{operations.signals.sources.http}</strong>
          </div>
          <div>
            <span>响应超时</span>
            <strong>{operations.incidents.overdue}</strong>
          </div>
          <div>
            <span>恢复可确认</span>
            <strong>{operations.incidents.recoveryReady}</strong>
          </div>
        </section>
      ) : null}

      {error ? <div className="action-error">{error}</div> : null}

      <section className="monitor-table">
        <div className="monitor-table-head">
          <span>Qode</span>
          <span>DNS / TLS</span>
          <span>HTTP</span>
          <span>延迟</span>
          <span>检查时间</span>
          <span>操作</span>
        </div>

        {loading ? (
          <div className="drawer-loading">
            <LoaderCircle className="spin" size={18} />
            正在读取监测记录
          </div>
        ) : null}

        {!loading && !monitorableQodes.length ? (
          <div className="monitor-empty">
            <Server size={21} />
            当前没有可监测的已发布 Qode
          </div>
        ) : null}

        {!loading
          ? monitorableQodes.map((qode) => {
              const latest = latestByQode.get(qode.id);
              return (
                <article className="monitor-row" key={qode.id}>
                  <div className="monitor-qode">
                    <strong>{qode.name}</strong>
                    <span>/r/{qode.slug}</span>
                  </div>
                  <div className="monitor-signals">
                    {latest ? (
                      <>
                        <span
                          className={`signal-pill signal-${latest.evidence.signals?.dns?.status ?? "unknown"}`}
                          title="DNS 解析状态"
                        >
                          <Globe2 size={12} />
                          DNS
                        </span>
                        <span
                          className={`signal-pill signal-${latest.evidence.signals?.tls?.status ?? "unknown"}`}
                          title={
                            latest.evidence.signals?.tls?.daysRemaining !== undefined
                              ? `证书剩余 ${latest.evidence.signals.tls.daysRemaining} 天`
                              : "TLS 证书状态"
                          }
                        >
                          <ShieldCheck size={12} />
                          TLS
                        </span>
                      </>
                    ) : (
                      <span className="monitor-pending">
                        <Clock3 size={13} />
                        尚未检查
                      </span>
                    )}
                  </div>
                  <span className="monitor-value">
                    {latest?.httpStatus ?? "—"}
                  </span>
                  <span className="monitor-value">
                    {latest ? `${latest.latencyMs} ms` : "—"}
                  </span>
                  <span className="monitor-time">
                    {latest ? formatRelativeTime(latest.checkedAt) : "—"}
                  </span>
                  <button
                    className="secondary-button monitor-run"
                    type="button"
                    disabled={!capabilities.operate || working !== null}
                    onClick={() => void runCheck(qode)}
                  >
                    {working === qode.id ? (
                      <LoaderCircle className="spin" size={14} />
                    ) : (
                      <Play size={14} />
                    )}
                    立即检查
                  </button>
                </article>
              );
            })
          : null}
      </section>

      {checks.length ? (
        <section className="monitor-history">
          <div className="section-title">
            <div>
              <h2>最近检查</h2>
              <span>保留目标、状态、延迟与错误证据</span>
            </div>
          </div>
          <div className="monitor-history-list">
            {checks.slice(0, 8).map((check) => (
              <div key={check.id}>
                <span
                  className={`monitor-dot monitor-dot-${check.status}`}
                />
                <strong>{check.qode.name}</strong>
                <span>
                  {check.evidence.observed ?? check.errorCode}
                  {check.evidence.signals?.tls?.daysRemaining !== undefined
                    ? ` · TLS ${check.evidence.signals.tls.daysRemaining} 天`
                    : ""}
                </span>
                <small>
                  {check.latencyMs} ms · {formatRelativeTime(check.checkedAt)}
                </small>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
